import { inputToTags, tagsToInput } from "../../mst/kinds/utils";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../schema/types/base";
import { PolicyCreate } from "../../schema/types/policy/create";
import { Policy } from "../../schema/types/policy/entity";
import { PolicyInput } from "../../schema/types/policy/input";
import { getDefaultQueryInput, inputToQuery, queryToInput } from "../../schema/types/query/input";
import { parseLink } from "../../utils/linkParser/linkParser";
import { v4 as uuidv4 } from "uuid";

export function getDefaultPolicyInput(): PolicyInput {
  return {
    ...getDefaultBaseInput(),
    id: "",
    version: 0,
    bindings: [],
    targetAll: false,
    targetKind: "agent",
    targets: [],
    targetQuery: getDefaultQueryInput(),
    meta: {
      queryResult: {
        agent: {
          data: null,
          isCollapsed: true,
        },
        auditctx: {
          data: null,
          isCollapsed: true,
        },
        cloudaccount: {
          data: null,
          isCollapsed: true,
        },
        domain: {
          data: null,
          isCollapsed: true,
        },
        group: {
          data: null,
          isCollapsed: true,
        },
        gvc: {
          data: null,
          isCollapsed: true,
        },
        identity: {
          data: null,
          isCollapsed: true,
        },
        image: {
          data: null,
          isCollapsed: true,
        },
        ipset: {
          data: null,
          isCollapsed: true,
        },
        location: {
          data: null,
          isCollapsed: true,
        },
        mk8s: {
          data: null,
          isCollapsed: true,
        },
        policy: {
          data: null,
          isCollapsed: true,
        },
        quota: {
          data: null,
          isCollapsed: true,
        },
        secret: {
          data: null,
          isCollapsed: true,
        },
        serviceaccount: {
          data: null,
          isCollapsed: true,
        },
        user: {
          data: null,
          isCollapsed: true,
        },
        volumeset: {
          data: null,
          isCollapsed: true,
        },
        workload: {
          data: null,
          isCollapsed: true,
        },
      },
      isEdit: false,
      permissionsWithImplied: {},
    },
  };
}

export function inputToPolicy(input: PolicyInput): PolicyCreate {
  const res: PolicyCreate = {
    name: input.name,
    kind: "policy",
    tags: inputToTags(input.tags),
    origin: "default",
    bindings: input.bindings.map((b) => ({
      permissions: b.permissions.map((p) => p.value),
      principalLinks: b.principalLinks.map((p) => p.link),
    })),
    targetKind: input.targetKind as any,
  };
  if (input.targetAll) {
    res.target = "all";
  } else {
    res.targetLinks = input.targets.map((t) => t.link);
    if (input.targetQuery.isEnabled) {
      res.targetQuery = inputToQuery(input.targetQuery, input.targetKind);
    }
  }
  if (input.description) {
    res.description = input.description;
  }
  return res;
}

export function policyToInput(policy: Policy): PolicyInput {
  const def = getDefaultPolicyInput();
  const res: PolicyInput = {
    ...def,
    name: policy.name,
    description: policy.description,
    id: policy.id,
    version: policy.version,
    tags: tagsToInput(policy.tags),
    bindings: (policy.bindings || []).map((b) => ({
      permissions: b.permissions.map((p) => ({ id: uuidv4(), value: p })),
      principalLinks: b.principalLinks.map((p) => {
        const { name, isGVCScoped, gvcRelative, absolute } = parseLink(p, { useInputCtx: true });
        return {
          value: isGVCScoped ? gvcRelative : name,
          name: name,
          link: absolute,
        };
      }),
    })),
    targetAll: policy.target === "all",
    targets: (policy.targetLinks || []).map((t) => {
      const { name, isGVCScoped, gvcRelative, absolute } = parseLink(t, { useInputCtx: true });
      return {
        value: isGVCScoped ? gvcRelative : name,
        name: name,
        link: absolute,
      };
    }),
    targetQuery: queryToInput(policy.targetQuery),
    targetKind: policy.targetKind as any,
    meta: {
      ...def.meta,
      isEdit: true,
    },
  };
  return res;
}

export const TargetKindOptions = [
  { label: "Agent", value: "agent" },
  { label: "Audit Context", value: "auditctx" },
  { label: "Cloud Account", value: "cloudaccount" },
  { label: "Domain", value: "domain" },
  { label: "Group", value: "group" },
  { label: "GVC", value: "gvc" },
  { label: "Identity", value: "identity" },
  { label: "Image", value: "image" },
  { label: "IP Set", value: "ipset" },
  { label: "Kubernetes", value: "mk8s" },
  { label: "Location", value: "location" },
  { label: "Org", value: "org" },
  { label: "Policy", value: "policy" },
  { label: "Quota", value: "quota" },
  { label: "Secret", value: "secret" },
  { label: "Service Account", value: "serviceaccount" },
  { label: "Task", value: "task" },
  { label: "User", value: "user" },
  { label: "Volume Set", value: "volumeset" },
  { label: "Workload", value: "workload" },
];

interface GetPrincipalLineResultItem {
  isLabel: boolean;
  content: string;
  link?: string;
}
type GetPrincipalLineResult = GetPrincipalLineResultItem[];
export function getPrincipalLines(links: string[]): GetPrincipalLineResult {
  const res: GetPrincipalLineResult = [];

  const userLinks = links.filter((l) => l.includes(`/user/`));
  const groupLinks = links.filter((l) => l.includes(`/group/`));
  const identityLinks = links.filter((l) => l.includes(`/identity/`));
  const serviceaccountLinks = links.filter((l) => l.includes(`/serviceaccount/`));

  if (userLinks.length > 0) {
    res.push({ isLabel: true, content: "Users" });
    for (const userLink of userLinks) {
      const { name } = parseLink(userLink, { useInputCtx: true });
      res.push({ isLabel: false, content: name, link: userLink });
    }
  }

  if (groupLinks.length > 0) {
    res.push({ isLabel: true, content: "Groups" });
    for (const groupLink of groupLinks) {
      const { name } = parseLink(groupLink, { useInputCtx: true });
      res.push({ isLabel: false, content: name, link: groupLink });
    }
  }

  if (identityLinks.length > 0) {
    res.push({ isLabel: true, content: "Identities" });
    for (const identityLink of identityLinks) {
      const { name } = parseLink(identityLink, { useInputCtx: true });
      res.push({ isLabel: false, content: name, link: identityLink });
    }
  }

  if (serviceaccountLinks.length > 0) {
    res.push({ isLabel: true, content: "Service Accounts" });
    for (const serviceaccountLink of serviceaccountLinks) {
      const { name } = parseLink(serviceaccountLink, { useInputCtx: true });
      res.push({ isLabel: false, content: name, link: serviceaccountLink });
    }
  }

  return res;
}

export function policyToPatch(_policy: PolicyCreate, current: Policy, concurrency: OptimisticConcurrency) {
  const policy: any = JSON.parse(JSON.stringify(_policy));

  policy[`$replace/targetLinks`] = policy.targetLinks;
  delete policy.targetLinks;

  if (current.targetQuery && !_policy.targetQuery) {
    policy.targetQuery = null as any;
  }

  if (current.target && !_policy.target) {
    policy.target = null as any;
  }

  policy[`$replace/bindings`] = policy.bindings;
  delete policy.bindings;

  policy[`$replace/tags`] = policy.tags;
  delete policy.tags;

  policy.id = concurrency.id;
  policy.version = concurrency.version;

  return policy;
}
